/**
 * API service for Google Workspace integration
 */

import { apiGet, apiPost, apiDelete } from '../../api/axiosInstance';
import { getMainDomainOrigin } from '../../utils/platform';
import { publicAxios } from '../../utils/oauthCrossDomain';
import type { GoogleOAuthOriginResolveResponse } from '../../../generated/api/models/googleOAuthOriginResolveResponse';

const API_BASE = '/api/v1/integrations/google';

export interface GoogleCredentials {
  client_id: string;
  client_secret: string;
}

export interface GoogleIntegrationStatus {
  google_connected: boolean;
  google_credentials_configured: boolean;
  gmail_enabled: boolean;
  drive_enabled: boolean;
  sheets_enabled: boolean;
  docs_enabled: boolean;
  calendar_enabled: boolean;
  connected_email?: string;
  google_connection_error?: string;
  microsoft_connected?: boolean;
  microsoft_connection_error?: string;
}

export interface GmailMessage {
  id: string;
  thread_id: string;
  subject: string;
  from: string;
  to: string;
  date: string;
  snippet: string;
  body?: string;
  is_read: boolean;
  labels: string[];
}

export interface GmailListResponse {
  messages: GmailMessage[];
  next_page_token?: string;
  total_count: number;
}

export interface DriveFile {
  id: string;
  name: string;
  mime_type: string;
  size?: number;
  created_time: string;
  modified_time: string;
  web_view_link?: string;
  icon_link?: string;
  parents?: string[];
}

export interface DriveListResponse {
  files: DriveFile[];
  next_page_token?: string;
}

export const googleIntegrationApi = {
  // Integration status
  getStatus: async (): Promise<{ data: GoogleIntegrationStatus }> => {
    return apiGet(`${API_BASE}/status`);
  },

  // Save OAuth credentials
  saveCredentials: async (credentials: GoogleCredentials): Promise<{ data: { success: boolean } }> => {
    return apiPost(`${API_BASE}/credentials`, credentials);
  },

  // Initiate OAuth flow (unified approach-A cross-domain).
  // Google's ONLY registered redirect URL is on the canonical app host, so we pin
  // redirect_uri there for every domain (main / sysadmin / custom). The backend
  // records the originating SPA origin in the one-time OAuth state; the canonical-
  // host callback page bounces back there to complete under the admin's session.
  // When scopes is omitted, the backend uses GoogleAuthProvider.get_default_scopes()
  // — the single source of truth that must match the Google Cloud Console
  // OAuth consent screen registration.
  initiateAuth: async (scopes?: string[]): Promise<{ data: { auth_url: string } }> => {
    const redirectUri = `${getMainDomainOrigin()}/oauth/callback/google`;
    return apiPost(`${API_BASE}/auth`, { scopes, redirect_uri: redirectUri });
  },

  // Cross-domain OAuth: the canonical-host callback page asks which domain the
  // admin STARTED the connect on so it can bounce back there to complete under
  // their tenant session. Unauthenticated (keyed by the one-time OAuth state);
  // a forged/expired state resolves to null (no bounce). See OAuthCallbackPage.
  resolveOAuthOrigin: async (state: string): Promise<string | null> => {
    const res = await publicAxios.post<GoogleOAuthOriginResolveResponse>(
      `${API_BASE}/oauth/resolve-origin`,
      { state },
    );
    return res.data.origin_domain ?? null;
  },

  // Complete OAuth: exchange the authorization code (authenticated, on the domain
  // that owns the admin's session). The backend verifies the one-time state and
  // uses the canonical redirect_uri it recorded at init time.
  completeAuth: async (code: string, state: string): Promise<{ data: unknown }> => {
    return apiPost(`${API_BASE}/callback`, { code, state });
  },

  // Disconnect Google account
  disconnect: async (): Promise<void> => {
    return apiDelete(`${API_BASE}/disconnect`);
  },

  // Gmail APIs
  gmail: {
    list: async (params?: { page_token?: string; max_results?: number; query?: string }): Promise<{ data: GmailListResponse }> => {
      const searchParams = new URLSearchParams();
      if (params?.page_token) searchParams.set('page_token', params.page_token);
      if (params?.max_results) searchParams.set('max_results', String(params.max_results));
      if (params?.query) searchParams.set('q', params.query);

      const url = `${API_BASE}/gmail/messages${searchParams.toString() ? `?${searchParams}` : ''}`;
      return apiGet(url);
    },

    get: async (messageId: string): Promise<{ data: GmailMessage }> => {
      return apiGet(`${API_BASE}/gmail/messages/${messageId}`);
    },
  },

  // Google Drive APIs
  drive: {
    list: async (params?: { page_token?: string; max_results?: number; folder_id?: string; query?: string }): Promise<{ data: DriveListResponse }> => {
      const searchParams = new URLSearchParams();
      if (params?.page_token) searchParams.set('page_token', params.page_token);
      if (params?.max_results) searchParams.set('max_results', String(params.max_results));
      if (params?.folder_id) searchParams.set('folder_id', params.folder_id);
      if (params?.query) searchParams.set('q', params.query);

      const url = `${API_BASE}/drive/files${searchParams.toString() ? `?${searchParams}` : ''}`;
      return apiGet(url);
    },

    get: async (fileId: string): Promise<{ data: DriveFile }> => {
      return apiGet(`${API_BASE}/drive/files/${fileId}`);
    },
  },
};
